import React, { useEffect, useState } from "react";
import { Button, Dropdown, Menu, Tooltip } from "antd";
import { GlobalOutlined } from "@ant-design/icons";
import "./index.css";
import { useTranslation } from "react-i18next";

type FontSize = "small" | "default" | "large";

interface AccessibilityControlsProps {
  showLanguage?: boolean;
}

const AccessibilityControls: React.FC<AccessibilityControlsProps> = ({
  showLanguage = true,
}) => {
  const [fontSize, setFontSize] = useState<FontSize>("default");
  const [highContrast, setHighContrast] = useState(false);

  const { i18n, t } = useTranslation();

  /*
   * Keep the selected language in sync with i18next.
   */
  const [language, setLanguage] = useState(
    i18n.language || localStorage.getItem("i18nextLng") || "en"
  );

  /*
   * Listen for language changes.
   *
   * This is important because the language can be changed
   * from AccessibilityControls or from another component.
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  /*
   * Change language
   */
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);

    localStorage.setItem("i18nextLng", lng);

    setLanguage(lng);
  };

  /*
   * Get translated language name
   */
  const getLanguageLabel = () => {
    switch (language) {
      case "hi":
        return t("lblHindi");

      case "mr":
        return t("lblMarathi");

      case "en":
      default:
        return t("lblEnglish");
    }
  };

  useEffect(() => {
    const savedFontSize =
      (localStorage.getItem("aa_font_size") as FontSize) || "default";

    const savedContrast =
      localStorage.getItem("aa_high_contrast") === "true";

    setFontSize(savedFontSize);
    setHighContrast(savedContrast);

    document.documentElement.classList.remove(
      "aa-font-small",
      "aa-font-default",
      "aa-font-large",
      "aa-high-contrast"
    );

    document.documentElement.classList.add(
      `aa-font-${savedFontSize}`
    );

    if (savedContrast) {
      document.documentElement.classList.add(
        "aa-high-contrast"
      );
    }
  }, []);

  const changeFontSize = (size: FontSize) => {
    setFontSize(size);

    localStorage.setItem("aa_font_size", size);

    document.documentElement.classList.remove(
      "aa-font-small",
      "aa-font-default",
      "aa-font-large"
    );

    document.documentElement.classList.add(
      `aa-font-${size}`
    );
  };

  const toggleContrast = () => {
    const newValue = !highContrast;

    setHighContrast(newValue);

    localStorage.setItem(
      "aa_high_contrast",
      String(newValue)
    );

    document.documentElement.classList.toggle(
      "aa-high-contrast",
      newValue
    );
  };

  const languageMenu = (
    <Menu
      selectedKeys={[language]}
      onClick={({ key }) => changeLanguage(String(key))}
    >
      <Menu.Item key="en">
        {t("lblEnglish")}
      </Menu.Item>

      <Menu.Item key="hi">
        {t("lblHindi")}
      </Menu.Item>

      <Menu.Item key="mr">
        {t("lblMarathi")}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="accessibilityControls">

      {/* FIRST ROW */}

      <div>
        <Tooltip title="Decrease text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "small" ? "active" : ""
            }`}
            onClick={() => changeFontSize("small")}
          >
            A<sup>−</sup>
          </Button>
        </Tooltip>

        <Tooltip title="Default text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "default" ? "active" : ""
            }`}
            onClick={() => changeFontSize("default")}
          >
            A
          </Button>
        </Tooltip>

        <Tooltip title="Increase text size">
          <Button
            type="text"
            className={`fontControl ${
              fontSize === "large" ? "active" : ""
            }`}
            onClick={() => changeFontSize("large")}
          >
            A<sup>+</sup>
          </Button>
        </Tooltip>

        <Tooltip
          title={
            highContrast
              ? "Turn off high contrast"
              : "Turn on high contrast"
          }
        >
          <Button
            type="text"
            className={`contrastControl ${
              highContrast ? "active" : ""
            }`}
            onClick={toggleContrast}
          >
            ◐
          </Button>
        </Tooltip>
      </div>

      {/* SECOND ROW */}

      {showLanguage && (
        <div>
          <div className="accessibilityDivider" />

          <Dropdown
            overlay={languageMenu}
            trigger={["click"]}
          >
            <Button
              type="text"
              className="languageControl"
              aria-label={t("lblSelectLanguage")}
            >
              <GlobalOutlined />

              <span>
                {getLanguageLabel()}
              </span>

              <span className="languageArrow">
                ⌄
              </span>
            </Button>
          </Dropdown>
        </div>
      )}

    </div>
  );
};

export default AccessibilityControls;